import { ServiceRegistryFormData } from '@/app/registry/service-registry/_hooks/use-service-form-validation'
import { toast } from '@/components/ui/toaster'
import { useEditServiceRegistryMutation } from '@/hooks/api/use-edit-service-registry-mutation'
import { useTranslate } from '@tolgee/react'
import React from 'react'
import { useServiceOAuthConnection } from '../_hooks/use-service-oauth-connection'
import { type ServiceRegistryTableDef, ServiceToolsDialogData } from '../tab'
import { ServiceRegistryBaseDialog } from './base-dialog'

type EditServiceRegistryDialogProps = {
  open: boolean
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>
  setToolsDialogData: React.Dispatch<React.SetStateAction<ServiceToolsDialogData>>
  setIsToolsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
  serviceData: ServiceRegistryTableDef
}

export function EditServiceRegistryDialog({
  open,
  onOpenChange,
  setToolsDialogData,
  setIsToolsDialogOpen,
  serviceData,
}: EditServiceRegistryDialogProps) {
  const { serviceId } = serviceData
  const { t } = useTranslate()
  const { mutateAsync: updateServiceMutation, isPending: isServiceUpdatePending } =
    useEditServiceRegistryMutation({ serviceId })

  const { oauthInit, handleOAuthCallback, isConnecting, clearOAuthState, connectionState } =
    useServiceOAuthConnection({
      serviceId,
      onSuccess: () => {
        toast.success(t('serviceRegistry.dialog.toast.connectionSuccess'))
      },
      onError: (connectionError) => {
        toast.error(t('serviceRegistry.dialog.toast.connectionFailed'))
        console.error(connectionError)
      },
    })

  const onSubmit = async (formData: ServiceRegistryFormData) => {
    try {
      const { access_token, token_type, expires_in, refresh_token, scope } =
        connectionState?.tokenResponse || {}

      const response = await updateServiceMutation({
        name: formData.name,
        serviceType: formData.serviceType,
        url: formData.url,
        key: formData.key,
        access_token,
        token_type,
        refresh_token,
        scope: scope?.split(' '),
        expires_in,
        clientId: connectionState?.clientRegistration?.client_id,
        tokenUrl: connectionState?.metadata?.token_endpoint,
      })
      toast.success(t('serviceRegistry.editDialog.toast.success', { serviceName: formData?.name }))
      if (response?.tools?.length) {
        setToolsDialogData({
          serviceName: formData.name,
          tools: response?.tools || [],
        })
        setIsToolsDialogOpen(true)
      }
      onOpenChange(false)
    } catch (registrationError) {
      toast.error(t('serviceRegistry.editDialog.toast.error'))
    }
  }

  return (
    <ServiceRegistryBaseDialog
      isEditMode
      open={open}
      onOpenChange={onOpenChange}
      onSubmit={onSubmit}
      oauthInit={oauthInit}
      disabled={isServiceUpdatePending || isConnecting}
      loading={isServiceUpdatePending}
      connectionState={connectionState}
      isOAuthConnecting={isConnecting}
      handleOAuthCallback={handleOAuthCallback}
      clearOAuthState={clearOAuthState}
      serviceData={serviceData}
    />
  )
}
